import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header'
import Footer from '../components/Footer';
import {
  Ship,
  Clock,
  Shield,
  Globe,
  Package,
  DollarSign,
  Anchor,
  Container,
  Waves,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Star,
  TrendingUp,
  Target,
  Award,
  Send,
  Box,
  Truck,
  Scale,
  Leaf
} from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return <span>{prefix}{count}{suffix}</span>;
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 dark:border-gray-700">
    <button
      onClick={onClick}
      className="w-full py-5 flex items-center justify-between text-left hover:text-[#3ECDA3] transition-colors"
    >
      <span className="font-medium text-gray-900 dark:text-white pr-8">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-[#3ECDA3] flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const SeaTransportPage = () => {
  const { language } = useLanguage();
  const [openFAQ, setOpenFAQ] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    origin: '',
    destination: '',
    cargoType: '',
    containerType: '',
    volume: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const statsRef = useRef(null);
  const formRef = useRef(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    pt: {
      hero: {
        badge: 'TRANSPORTE MARÍTIMO INTERNACIONAL',
        title: 'Economia e Capacidade',
        titleHighlight: 'para Grandes Volumes',
        subtitle: 'A melhor relação custo-benefício para suas operações de importação e exportação. Conectamos o Brasil aos principais portos do mundo.',
        cta: 'SOLICITAR COTAÇÃO',
        ctaSecondary: 'FALAR COM ESPECIALISTA'
      },
      benefits: {
        title: 'Vantagens do',
        titleHighlight: 'Transporte Marítimo',
        items: [
          { icon: DollarSign, title: 'Menor custo por volume', desc: 'É o modal mais econômico para grandes quantidades de carga, especialmente quando comparado ao aéreo.' },
          { icon: Package, title: 'Alta capacidade de transporte', desc: 'Permite o embarque de grandes volumes, cargas pesadas, oversized e projetos especiais, com diferentes tipos de contêineres e navios.' },
          { icon: Box, title: 'Versatilidade de cargas', desc: 'Atende cargas secas, refrigeradas, perigosas, granel, máquinas, veículos e mercadorias de baixo ou alto valor agregado.' },
          { icon: Globe, title: 'Ampla cobertura global', desc: 'Conecta praticamente todos os mercados do mundo, com rotas consolidadas e grande oferta de armadores.' },
          { icon: Clock, title: 'Ideal para planejamento', desc: 'É indicado para operações sem urgência extrema, permitindo melhor planejamento logístico e financeiro de médio e longo prazo.' },
          { icon: Container, title: 'Opções de contêiner', desc: 'Disponibilidade de 20\', 40\', 40HC, reefers, open top, flat rack, entre outros, adequando-se à necessidade da carga.' },
          { icon: Leaf, title: 'Menor impacto ambiental', desc: 'Em comparação a outros modais, apresenta melhor eficiência energética por tonelada transportada.' },
          { icon: Scale, title: 'Consolidação e desconsolidação', desc: 'Permite embarques FCL e LCL, facilitando a otimização de custos para diferentes perfis de importadores e exportadores.' }
        ]
      },
      services: {
        title: 'Modalidades de',
        titleHighlight: 'Embarque',
        items: [
          { 
            icon: Container, 
            name: 'FCL - Full Container', 
            desc: 'Contêiner completo exclusivo para sua carga. Ideal para grandes volumes.',
            details: ['20\' e 40\' Standard', '40\' High Cube', 'Reefer (refrigerado)', 'Open Top & Flat Rack']
          },
          { 
            icon: Box, 
            name: 'LCL - Carga Consolidada', 
            desc: 'Compartilhe o contêiner e pague apenas pelo espaço utilizado.',
            details: ['Economia para pequenos volumes', 'Consolidação semanal', 'Principais rotas', 'Desconsolidação no destino']
          },
          { 
            icon: Anchor, 
            name: 'Break Bulk', 
            desc: 'Cargas soltas ou de grandes dimensões que não cabem em contêineres.',
            details: ['Máquinas e equipamentos', 'Estruturas metálicas', 'Bobinas e chapas', 'Cargas de projeto']
          },
          { 
            icon: Truck, 
            name: 'Ro-Ro', 
            desc: 'Roll-on/Roll-off para veículos e cargas sobre rodas.',
            details: ['Automóveis e caminhões', 'Máquinas agrícolas', 'Equipamentos pesados', 'Embarque direto']
          },
        ]
      },
      stats: {
        title: 'Nossa Operação',
        titleHighlight: 'Marítima',
        items: [
          { value: 24, suffix: 'Mil', label: 'Contêineres/ano' },
          { value: 95, suffix: '+', label: 'Países' },
          { value: 50, suffix: '+', label: 'Portos' },
          { value: 15, suffix: '+', label: 'Anos' }
        ]
      },
      process: {
        title: 'Como Funciona',
        steps: [
          { num: '01', title: 'Cotação', desc: 'Analisamos sua carga e enviamos proposta em até 24h' },
          { num: '02', title: 'Booking', desc: 'Reservamos espaço no navio com o melhor armador' },
          { num: '03', title: 'Coleta', desc: 'Retiramos a carga e preparamos documentação' },
          { num: '04', title: 'Embarque', desc: 'Acompanhamos todo o processo portuário' },
          { num: '05', title: 'Trânsito', desc: 'Acompanhamento da sua carga durante a viagem' },
          { num: '06', title: 'Entrega', desc: 'Desembaraço e entrega no destino final' }
        ]
      },
      routes: {
        title: 'Principais',
        titleHighlight: 'Rotas',
        items: [
          { region: 'Europa', ports: 'Rotterdam, Hamburgo, Antuérpia, Le Havre', time: '18-25 dias' },
          { region: 'Ásia', ports: 'Shanghai, Singapura, Hong Kong, Busan', time: '40-70 dias' },
          { region: 'América do Norte', ports: 'Nova York, Los Angeles, Miami, Houston', time: '15-22 dias' },
          { region: 'América do Sul', ports: 'Buenos Aires, Callao, Valparaíso', time: '5-12 dias' },
        ]
      },
      testimonial: {
        quote: 'A Well Cargo nos ajudou a reduzir 35% nos custos logísticos com soluções marítimas inteligentes. O suporte na documentação e desembaraço é impecável.',
        author: 'Marcos Oliveira',
        role: 'Gerente de Supply Chain',
        company: 'Indústria MetalBras'
      },
      faq: {
        title: 'Perguntas',
        titleHighlight: 'Frequentes',
        items: [
          { q: 'Quando o transporte marítimo é a melhor opção?', a: 'Quando a carga não é urgente, possui grande volume ou peso e o foco principal é a otimização de custos.' },
          { q: 'Quais tipos de carga podem ser transportados por via marítima?', a: 'Cargas gerais, granel, máquinas, veículos, projetos especiais, cargas perigosas, refrigeradas, oversized e mercadorias de baixo ou alto valor agregado.' },
          { q: 'O que é FCL e LCL?', a: 'FCL é o embarque de um contêiner exclusivo para um único cliente. LCL é o embarque consolidado, onde a carga divide o contêiner com mercadorias de outros embarcadores.' },
          { q: 'Existe limite de peso ou volume no transporte marítimo?', a: 'Sim. Cada tipo de contêiner possui limites máximos de peso e volume, que variam conforme o equipamento e o armador.' },
          { q: 'Qual é o prazo médio de trânsito no modal marítimo?', a: 'Depende da rota, do armador e das escalas, podendo variar de algumas semanas a mais de 40 dias em rotas intercontinentais.' },
          { q: 'O transporte marítimo aceita carga perigosa?', a: 'Sim, desde que a carga esteja classificada, embalada, rotulada e documentada conforme as normas internacionais e regras do armador.' },
          { q: 'Quais documentos são necessários no transporte marítimo internacional?', a: 'Normalmente: invoice, packing list, conhecimento de embarque (BL), documentos aduaneiros e certificados específicos, quando aplicável.' },
          { q: 'O transporte marítimo é seguro?', a: 'Sim. Apesar do maior tempo de trânsito, é um modal seguro quando bem planejado, com embalagens adequadas e seguro de carga.' },
          { q: 'É possível acompanhar a carga durante o transporte?', a: 'Sim. Os armadores disponibilizam sistemas de rastreamento que permitem acompanhar o status do contêiner ao longo da viagem.' },
          { q: 'O que é demurrage e free time?', a: 'Free time é o período gratuito para uso do contêiner. Demurrage é a cobrança aplicada quando esse prazo é ultrapassado.' },
          { q: 'Posso consolidar cargas no transporte marítimo?', a: 'Sim. A consolidação LCL é bastante comum e permite redução de custos para cargas menores.' },
          { q: 'O transporte marítimo é mais sustentável?', a: 'Em termos de emissão por tonelada transportada, o modal marítimo é considerado um dos mais eficientes energeticamente.' }
        ]
      },
      cta: {
        title: 'Pronto para Otimizar',
        titleHighlight: 'sua Logística Marítima?',
        subtitle: 'Preencha o formulário e receba uma cotação personalizada em até 24 horas.',
        badge: 'COTAÇÃO GRATUITA'
      },
      form: {
        name: 'Nome completo',
        email: 'E-mail corporativo',
        phone: 'WhatsApp',
        company: 'Empresa',
        origin: 'Porto de origem',
        destination: 'Porto de destino',
        cargoType: 'Tipo de carga',
        containerType: 'Tipo de embarque',
        volume: 'Volume estimado',
        message: 'Detalhes da carga (opcional)',
        submit: 'SOLICITAR COTAÇÃO GRÁTIS',
        success: 'Recebemos sua solicitação! Nossa equipe de comércio exterior entrará em contato em breve.',
        containerOptions: ['FCL 20\'', 'FCL 40\'', 'FCL 40\' HC', 'LCL (Consolidado)', 'Break Bulk', 'Ro-Ro', 'Reefer'],
        cargoOptions: ['Carga Geral', 'Máquinas/Equipamentos', 'Commodities', 'Químicos', 'Perecíveis', 'Veículos', 'Outros']
      },
      contact: {
        title: 'Prefere falar diretamente?',
        phone: '+55 27 99758-0648',
        whatsapp: '+55 31 99592-5853',
        email: 'maritimo@wellcargo.com.br'
      }
    },
    en: {
      hero: {
        badge: 'INTERNATIONAL SEA TRANSPORT',
        title: 'Economy and Capacity',
        titleHighlight: 'for Large Volumes',
        subtitle: 'The best cost-benefit ratio for your import and export operations. We connect Brazil to major ports worldwide.',
        cta: 'REQUEST QUOTE',
        ctaSecondary: 'TALK TO SPECIALIST'
      },
      benefits: {
        title: 'Advantages of',
        titleHighlight: 'Sea Transport',
        items: [
          { icon: DollarSign, title: 'Lower cost per volume', desc: 'It is the most economical mode for large quantities of cargo, especially when compared to air.' },
          { icon: Package, title: 'High transport capacity', desc: 'Allows the shipment of large volumes, heavy cargo, oversized and special projects, with different types of containers and vessels.' },
          { icon: Box, title: 'Cargo versatility', desc: 'Handles dry, refrigerated, dangerous, bulk cargo, machines, vehicles and goods of low or high added value.' },
          { icon: Globe, title: 'Wide global coverage', desc: 'Connects virtually all markets in the world, with consolidated routes and wide range of carriers.' },
          { icon: Clock, title: 'Ideal for planning', desc: 'Indicated for operations without extreme urgency, allowing better medium and long-term logistics and financial planning.' },
          { icon: Container, title: 'Container options', desc: 'Availability of 20\', 40\', 40HC, reefers, open top, flat rack, among others, adapting to cargo needs.' },
          { icon: Leaf, title: 'Lower environmental impact', desc: 'Compared to other modes, it presents better energy efficiency per ton transported.' },
          { icon: Scale, title: 'Consolidation and deconsolidation', desc: 'Allows FCL and LCL shipments, facilitating cost optimization for different importer and exporter profiles.' }
        ]
      },
      services: {
        title: 'Shipping',
        titleHighlight: 'Modalities',
        items: [
          { 
            icon: Container, 
            name: 'FCL - Full Container', 
            desc: 'Exclusive full container for your cargo. Ideal for large volumes.',
            details: ['20\' and 40\' Standard', '40\' High Cube', 'Reefer (refrigerated)', 'Open Top & Flat Rack']
          },
          { 
            icon: Box, 
            name: 'LCL - Consolidated Cargo', 
            desc: 'Share the container and pay only for the space used.',
            details: ['Economy for small volumes', 'Weekly consolidation', 'Main routes', 'Deconsolidation at destination']
          },
          { 
            icon: Anchor, 
            name: 'Break Bulk', 
            desc: 'Loose cargo or large dimensions that don\'t fit in containers.',
            details: ['Machines and equipment', 'Metal structures', 'Coils and plates', 'Project cargo']
          },
          { 
            icon: Truck, 
            name: 'Ro-Ro', 
            desc: 'Roll-on/Roll-off for vehicles and wheeled cargo.',
            details: ['Cars and trucks', 'Agricultural machinery', 'Heavy equipment', 'Direct loading']
          },
        ]
      },
      stats: {
        title: 'Our Maritime',
        titleHighlight: 'Operation',
        items: [
          { value: 24, suffix: 'K', label: 'Containers/year' },
          { value: 95, suffix: '+', label: 'Countries' },
          { value: 50, suffix: '+', label: 'Ports' },
          { value: 15, suffix: '+', label: 'Years' }
        ]
      },
      process: {
        title: 'How it Works',
        steps: [
          { num: '01', title: 'Quote', desc: 'We analyze your cargo and send proposal within 24h' },
          { num: '02', title: 'Booking', desc: 'We reserve space on the ship with the best carrier' },
          { num: '03', title: 'Pickup', desc: 'We collect cargo and prepare documentation' },
          { num: '04', title: 'Shipping', desc: 'We accompany the entire port process' },
          { num: '05', title: 'Transit', desc: 'Real-time monitoring of your cargo' },
          { num: '06', title: 'Delivery', desc: 'Customs clearance and delivery to final destination' }
        ]
      },
      routes: {
        title: 'Main',
        titleHighlight: 'Routes',
        items: [
          { region: 'Europe', ports: 'Rotterdam, Hamburg, Antwerp, Le Havre', time: '18-25 days' },
          { region: 'Asia', ports: 'Shanghai, Singapore, Hong Kong, Busan', time: '40-70 days' },
          { region: 'North America', ports: 'New York, Los Angeles, Miami, Houston', time: '15-22 days' },
          { region: 'South America', ports: 'Buenos Aires, Callao, Valparaiso', time: '5-12 days' },
        ]
      },
      testimonial: {
        quote: 'Well Cargo helped us reduce logistics costs by 35% with smart maritime solutions. Documentation and customs support is impeccable.',
        author: 'Marcos Oliveira',
        role: 'Supply Chain Manager',
        company: 'MetalBras Industry'
      },
      faq: {
        title: 'Frequently Asked',
        titleHighlight: 'Questions',
        items: [
          { q: 'When is sea transport the best option?', a: 'When the cargo is not urgent, has large volume or weight and the main focus is cost optimization.' },
          { q: 'What types of cargo can be transported by sea?', a: 'General cargo, bulk, machines, vehicles, special projects, dangerous, refrigerated, oversized cargo and goods of low or high added value.' },
          { q: 'What is FCL and LCL?', a: 'FCL is the shipment of an exclusive container for a single customer. LCL is consolidated shipment, where cargo shares the container with goods from other shippers.' },
          { q: 'Are there weight or volume limits in sea transport?', a: 'Yes. Each container type has maximum weight and volume limits, which vary according to equipment and carrier.' },
          { q: 'What is the average transit time for sea freight?', a: 'Depends on the route, carrier and stops, ranging from a few weeks to more than 40 days on intercontinental routes.' },
          { q: 'Does sea transport accept dangerous cargo?', a: 'Yes, as long as the cargo is classified, packaged, labeled and documented according to international standards and carrier rules.' },
          { q: 'What documents are required for international sea transport?', a: 'Usually: invoice, packing list, bill of lading (BL), customs documents and specific certificates when applicable.' },
          { q: 'Is sea transport safe?', a: 'Yes. Despite longer transit time, it is a safe mode when well planned, with adequate packaging and cargo insurance.' },
          { q: 'Is it possible to track cargo during transport?', a: 'Yes. Carriers provide tracking systems that allow monitoring container status throughout the voyage.' },
          { q: 'What is demurrage and free time?', a: 'Free time is the free period for container use. Demurrage is the charge applied when this period is exceeded.' },
          { q: 'Can I consolidate cargo in sea transport?', a: 'Yes. LCL consolidation is quite common and allows cost reduction for smaller cargo.' },
          { q: 'Is sea transport more sustainable?', a: 'In terms of emission per ton transported, sea freight is considered one of the most energy efficient modes.' }
        ]
      },
      cta: {
        title: 'Ready to Optimize',
        titleHighlight: 'your Maritime Logistics?',
        subtitle: 'Fill out the form and receive a personalized quote within 24 hours.',
        badge: 'FREE QUOTE'
      },
      form: {
        name: 'Full name',
        email: 'Corporate email',
        phone: 'WhatsApp',
        company: 'Company',
        origin: 'Origin port',
        destination: 'Destination port',
        cargoType: 'Cargo type',
        containerType: 'Shipping type',
        volume: 'Estimated volume',
        message: 'Cargo details (optional)',
        submit: 'REQUEST FREE QUOTE',
        success: 'We received your request! Our foreign trade team will contact you soon.',
        containerOptions: ['FCL 20\'', 'FCL 40\'', 'FCL 40\' HC', 'LCL (Consolidated)', 'Break Bulk', 'Ro-Ro', 'Reefer'],
        cargoOptions: ['General Cargo', 'Machines/Equipment', 'Commodities', 'Chemicals', 'Perishables', 'Vehicles', 'Others']
      },
      contact: {
        title: 'Prefer to talk directly?',
        phone: '+55 27 99758-0648',
        whatsapp: '+55 31 99592-5853',
        email: 'maritimo@wellcargo.com.br'
      }
    },
    es: {
      hero: {
        badge: 'TRANSPORTE MARÍTIMO INTERNACIONAL',
        title: 'Economía y Capacidad',
        titleHighlight: 'para Grandes Volúmenes',
        subtitle: 'La mejor relación costo-beneficio para sus operaciones de importación y exportación. Conectamos Brasil con los principales puertos del mundo.',
        cta: 'SOLICITAR COTIZACIÓN',
        ctaSecondary: 'HABLAR CON ESPECIALISTA'
      },
      benefits: {
        title: 'Ventajas del',
        titleHighlight: 'Transporte Marítimo',
        items: [
          { icon: DollarSign, title: 'Menor costo por volumen', desc: 'Es el modal más económico para grandes cantidades de carga, especialmente cuando se compara con el aéreo.' },
          { icon: Package, title: 'Alta capacidad de transporte', desc: 'Permite el embarque de grandes volúmenes, cargas pesadas, oversized y proyectos especiales, con diferentes tipos de contenedores y buques.' },
          { icon: Box, title: 'Versatilidad de cargas', desc: 'Atiende cargas secas, refrigeradas, peligrosas, granel, máquinas, vehículos y mercancías de bajo o alto valor agregado.' },
          { icon: Globe, title: 'Amplia cobertura global', desc: 'Conecta prácticamente todos los mercados del mundo, con rutas consolidadas y gran oferta de armadores.' },
          { icon: Clock, title: 'Ideal para planificación', desc: 'Es indicado para operaciones sin urgencia extrema, permitiendo mejor planificación logística y financiera de mediano y largo plazo.' },
          { icon: Container, title: 'Opciones de contenedor', desc: 'Disponibilidad de 20\', 40\', 40HC, reefers, open top, flat rack, entre otros, adecuándose a la necesidad de la carga.' },
          { icon: Leaf, title: 'Menor impacto ambiental', desc: 'En comparación con otros modales, presenta mejor eficiencia energética por tonelada transportada.' },
          { icon: Scale, title: 'Consolidación y desconsolidación', desc: 'Permite embarques FCL y LCL, facilitando la optimización de costos para diferentes perfiles de importadores y exportadores.' }
        ]
      },
      services: {
        title: 'Modalidades de',
        titleHighlight: 'Embarque',
        items: [
          { 
            icon: Container, 
            name: 'FCL - Contenedor Completo', 
            desc: 'Contenedor completo exclusivo para su carga. Ideal para grandes volúmenes.',
            details: ['20\' y 40\' Standard', '40\' High Cube', 'Reefer (refrigerado)', 'Open Top & Flat Rack']
          },
          { 
            icon: Box, 
            name: 'LCL - Carga Consolidada', 
            desc: 'Comparta el contenedor y pague solo por el espacio utilizado.',
            details: ['Economía para pequeños volúmenes', 'Consolidación semanal', 'Principales rutas', 'Desconsolidación en destino']
          },
          { 
            icon: Anchor, 
            name: 'Break Bulk', 
            desc: 'Cargas sueltas o de grandes dimensiones que no caben en contenedores.',
            details: ['Máquinas y equipos', 'Estructuras metálicas', 'Bobinas y chapas', 'Cargas de proyecto']
          },
          { 
            icon: Truck, 
            name: 'Ro-Ro', 
            desc: 'Roll-on/Roll-off para vehículos y cargas sobre ruedas.',
            details: ['Automóviles y camiones', 'Maquinaria agrícola', 'Equipos pesados', 'Embarque directo']
          },
        ]
      },
      stats: {
        title: 'Nuestra Operación',
        titleHighlight: 'Marítima',
        items: [
          { value: 24, suffix: 'Mil', label: 'Contenedores/año' },
          { value: 95, suffix: '+', label: 'Países' },
          { value: 50, suffix: '+', label: 'Puertos' },
          { value: 15, suffix: '+', label: 'Años' }
        ]
      },
      process: {
        title: 'Cómo Funciona',
        steps: [
          { num: '01', title: 'Cotización', desc: 'Analizamos su carga y enviamos propuesta en 24h' },
          { num: '02', title: 'Booking', desc: 'Reservamos espacio en el barco con el mejor armador' },
          { num: '03', title: 'Recogida', desc: 'Retiramos la carga y preparamos documentación' },
          { num: '04', title: 'Embarque', desc: 'Acompañamos todo el proceso portuario' },
          { num: '05', title: 'Tránsito', desc: 'Monitoreo en tiempo real de su carga' },
          { num: '06', title: 'Entrega', desc: 'Despacho y entrega en destino final' }
        ]
      },
      routes: {
        title: 'Principales',
        titleHighlight: 'Rutas',
        items: [
          { region: 'Europa', ports: 'Rotterdam, Hamburgo, Amberes, Le Havre', time: '18-25 días' },
          { region: 'Asia', ports: 'Shanghai, Singapur, Hong Kong, Busan', time: '40-70 días' },
          { region: 'América del Norte', ports: 'Nueva York, Los Ángeles, Miami, Houston', time: '15-22 días' },
          { region: 'América del Sur', ports: 'Buenos Aires, Callao, Valparaíso', time: '5-12 días' },
        ]
      },
      testimonial: {
        quote: 'Well Cargo nos ayudó a reducir 35% en costos logísticos con soluciones marítimas inteligentes. El soporte en documentación y despacho es impecable.',
        author: 'Marcos Oliveira',
        role: 'Gerente de Supply Chain',
        company: 'Industria MetalBras'
      },
      faq: {
        title: 'Preguntas',
        titleHighlight: 'Frecuentes',
        items: [
          { q: '¿Cuándo es el transporte marítimo la mejor opción?', a: 'Cuando la carga no es urgente, tiene gran volumen o peso y el enfoque principal es la optimización de costos.' },
          { q: '¿Qué tipos de carga pueden transportarse por vía marítima?', a: 'Cargas generales, granel, máquinas, vehículos, proyectos especiales, cargas peligrosas, refrigeradas, oversized y mercancías de bajo o alto valor agregado.' },
          { q: '¿Qué es FCL y LCL?', a: 'FCL es el embarque de un contenedor exclusivo para un único cliente. LCL es el embarque consolidado, donde la carga comparte el contenedor con mercancías de otros embarcadores.' },
          { q: '¿Existe límite de peso o volumen en el transporte marítimo?', a: 'Sí. Cada tipo de contenedor tiene límites máximos de peso y volumen, que varían según el equipo y el armador.' },
          { q: '¿Cuál es el plazo medio de tránsito en el modal marítimo?', a: 'Depende de la ruta, del armador y de las escalas, pudiendo variar de algunas semanas a más de 40 días en rutas intercontinentales.' },
          { q: '¿El transporte marítimo acepta carga peligrosa?', a: 'Sí, siempre que la carga esté clasificada, embalada, etiquetada y documentada conforme a las normas internacionales y reglas del armador.' },
          { q: '¿Qué documentos son necesarios en el transporte marítimo internacional?', a: 'Normalmente: invoice, packing list, conocimiento de embarque (BL), documentos aduaneros y certificados específicos cuando aplique.' },
          { q: '¿El transporte marítimo es seguro?', a: 'Sí. A pesar del mayor tiempo de tránsito, es un modal seguro cuando está bien planificado, con embalajes adecuados y seguro de carga.' },
          { q: '¿Es posible acompañar la carga durante el transporte?', a: 'Sí. Los armadores proporcionan sistemas de rastreo que permiten acompañar el estado del contenedor a lo largo del viaje.' },
          { q: '¿Qué es demurrage y free time?', a: 'Free time es el período gratuito para uso del contenedor. Demurrage es el cargo aplicado cuando ese plazo es superado.' },
          { q: '¿Puedo consolidar cargas en el transporte marítimo?', a: 'Sí. La consolidación LCL es bastante común y permite reducción de costos para cargas menores.' },
          { q: '¿El transporte marítimo es más sustentable?', a: 'En términos de emisión por tonelada transportada, el modal marítimo es considerado uno de los más eficientes energéticamente.' }
        ]
      },
      cta: {
        title: '¿Listo para Optimizar',
        titleHighlight: 'su Logística Marítima?',
        subtitle: 'Complete el formulario y reciba una cotización personalizada en 24 horas.',
        badge: 'COTIZACIÓN GRATIS'
      },
      form: {
        name: 'Nombre completo',
        email: 'Correo corporativo',
        phone: 'WhatsApp',
        company: 'Empresa',
        origin: 'Puerto de origen',
        destination: 'Puerto de destino',
        cargoType: 'Tipo de carga',
        containerType: 'Tipo de embarque',
        volume: 'Volumen estimado',
        message: 'Detalles de la carga (opcional)',
        submit: 'SOLICITAR COTIZACIÓN GRATIS',
        success: '¡Recibimos su solicitud! Nuestro equipo de comercio exterior lo contactará pronto.',
        containerOptions: ['FCL 20\'', 'FCL 40\'', 'FCL 40\' HC', 'LCL (Consolidado)', 'Break Bulk', 'Ro-Ro', 'Reefer'],
        cargoOptions: ['Carga General', 'Máquinas/Equipos', 'Commodities', 'Químicos', 'Perecederos', 'Vehículos', 'Otros']
      },
      contact: {
        title: '¿Prefiere hablar directamente?',
        phone: '+55 27 99758-0648',
        whatsapp: '+55 31 99592-5853',
        email: 'maritimo@wellcargo.com.br'
      }
    }
  };

  const c = content[language];

  // Intersection observer for stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      console.log('Sea transport lead form submitted:', formData);
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', origin: '', destination: '', cargoType: '', containerType: '', volume: '', message: '' });
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-900/70 to-transparent" />
        </div>

        {/* Animated waves */}
        <div className="absolute bottom-0 left-0 right-0 h-32 overflow-hidden">
          <svg className="absolute bottom-0 w-full h-24 text-white/5" viewBox="0 0 1440 100" preserveAspectRatio="none">
            <path d="M0,50 C150,100 350,0 500,50 C650,100 850,0 1000,50 C1150,100 1350,0 1440,50 L1440,100 L0,100 Z" fill="currentColor">
              <animate attributeName="d" dur="10s" repeatCount="indefinite" values="M0,50 C150,100 350,0 500,50 C650,100 850,0 1000,50 C1150,100 1350,0 1440,50 L1440,100 L0,100 Z;M0,50 C150,0 350,100 500,50 C650,0 850,100 1000,50 C1150,0 1350,100 1440,50 L1440,100 L0,100 Z;M0,50 C150,100 350,0 500,50 C650,100 850,0 1000,50 C1150,100 1350,0 1440,50 L1440,100 L0,100 Z" />
            </path>
          </svg>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 animate-fade-in">
              <Ship className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">{c.hero.badge}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {c.hero.title}<br />
              <span className="text-cyan-400 italic">{c.hero.titleHighlight}</span>
            </h1>

            <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: '0.4s' }}>
              {c.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <button
                onClick={scrollToForm}
                className="group bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/30"
              >
                {c.hero.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="https://wa.me/5531995925853"
                target="_blank"
                rel="noopener noreferrer"
                className="group border-2 border-white/30 hover:border-cyan-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:bg-cyan-500/10"
              >
                <MessageCircle className="w-5 h-5" />
                {c.hero.ctaSecondary}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <button onClick={scrollToForm} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white animate-bounce">
          <ChevronDown size={32} />
        </button>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.benefits.title} <span className="text-cyan-500 italic">{c.benefits.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {c.benefits.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div
                  key={index}
                  className="group bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700"
                >
                  <div className="w-14 h-14 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-500 transition-colors duration-300">
                    <IconComp className="w-7 h-7 text-cyan-500 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services/Modalities Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.services.title} <span className="text-cyan-500 italic">{c.services.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {c.services.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div key={index} className="group bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 hover:border-cyan-500/30 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <IconComp className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{item.name}</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">{item.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {item.details.map((detail, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 px-3 py-1 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {c.stats.title} <span className="italic text-cyan-400">{c.stats.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {c.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={statsVisible} />
                </div>
                <p className="text-cyan-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Routes Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.routes.title} <span className="text-cyan-500 italic">{c.routes.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.routes.items.map((route, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:border-cyan-500/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-cyan-500" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{route.region}</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{route.ports}</p>
                <div className="flex items-center gap-2 text-cyan-500">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm font-medium">{route.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-cyan-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-cyan-400 text-cyan-400" />
            ))}
          </div>
          <blockquote className="text-2xl md:text-3xl text-white italic leading-relaxed mb-8">
            "{c.testimonial.quote}"
          </blockquote>
          <div>
            <p className="font-bold text-white">{c.testimonial.author}</p>
            <p className="text-cyan-400">{c.testimonial.role}, {c.testimonial.company}</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center mb-12">
            {c.faq.title} <span className="text-cyan-500 italic">{c.faq.titleHighlight}</span>
          </h2>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-6 md:p-8">
            {c.faq.items.map((item, index) => (
              <FAQItem
                key={index}
                question={item.q}
                answer={item.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA & Form Section */}
      <section ref={formRef} className="py-20 md:py-28 bg-gradient-to-br from-blue-900 to-cyan-800 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - CTA Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Ship className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">{c.cta.badge}</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {c.cta.title}<br />
                <span className="text-cyan-400 italic">{c.cta.titleHighlight}</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8">{c.cta.subtitle}</p>

              {/* Contact alternatives */}
              <div className="space-y-4">
                <p className="text-white/70 text-sm">{c.contact.title}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a href={`tel:${c.contact.phone}`} className="flex items-center gap-2 text-white hover:text-cyan-400 transition-colors">
                    <Phone className="w-5 h-5" />
                    <span>{c.contact.phone}</span>
                  </a>
                  <a href={`https://wa.me/${c.contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white hover:text-cyan-400 transition-colors">
                    <MessageCircle className="w-5 h-5" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Recebemos!</h3>
                  <p className="text-gray-600 dark:text-gray-400">{c.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text" name="name" required placeholder={c.form.name} value={formData.name} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="email" name="email" required placeholder={c.form.email} value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="tel" name="phone" required placeholder={c.form.phone} value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="text" name="company" placeholder={c.form.company} value={formData.company} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text" name="origin" required placeholder={c.form.origin} value={formData.origin} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="text" name="destination" required placeholder={c.form.destination} value={formData.destination} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <select
                      name="containerType" required value={formData.containerType} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    >
                      <option value="">{c.form.containerType}</option>
                      {c.form.containerOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                    <select
                      name="cargoType" required value={formData.cargoType} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    >
                      <option value="">{c.form.cargoType}</option>
                      {c.form.cargoOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <input
                    type="text" name="volume" placeholder={c.form.volume} value={formData.volume} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                  />
                  <textarea
                    name="message" rows={3} placeholder={c.form.message} value={formData.message} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none resize-none"
                  />
                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {c.form.submit}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeaTransportPage;
